import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { AuditAction, AuditLogEntry } from '../../types/audit';
import { AUDIT_ACTION_LABELS, AUDIT_ENTITY_LABELS } from '../../types/audit';
import { AuditDiffView } from './AuditDiffView';

const actionColor = (a: AuditAction): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (a) {
    case 'CREATE':
    case 'ASSIGN':
    case 'LOGIN_SUCCESS':
      return 'success';
    case 'UPDATE':
    case 'PASSWORD_CHANGE':
      return 'info';
    case 'DELETE':
    case 'REVOKE':
      return 'warning';
    case 'LOGIN_FAIL':
      return 'error';
    default:
      return 'default';
  }
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('cs-CZ');
  } catch {
    return iso;
  }
};

const Row = ({ entry }: { entry: AuditLogEntry }) => {
  const [open, setOpen] = useState(false);
  const hasDetails =
    (entry.oldValues && Object.keys(entry.oldValues).length > 0) ||
    (entry.newValues && Object.keys(entry.newValues).length > 0);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          {hasDetails && (
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>
              {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{formatDate(entry.changedAt)}</TableCell>
        <TableCell>
          <Stack>
            <Typography variant="body2">{entry.changedByUsername ?? '—'}</Typography>
            {entry.changedByRole && (
              <Typography variant="caption" color="text.secondary">
                {entry.changedByRole}
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Chip size="small" label={AUDIT_ENTITY_LABELS[entry.entityType]} variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip size="small" label={AUDIT_ACTION_LABELS[entry.action]} color={actionColor(entry.action)} />
        </TableCell>
        <TableCell sx={{ maxWidth: 400 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {entry.summary ?? '—'}
          </Typography>
          {entry.ipAddress && (
            <Typography variant="caption" color="text.secondary">
              IP: {entry.ipAddress}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                <AuditDiffView entry={entry} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface Props {
  entries: AuditLogEntry[];
  compact?: boolean;
}

export const AuditLogTable = ({ entries, compact = false }: Props) => {
  if (entries.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Žádné audit záznamy.</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size={compact ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Datum</TableCell>
            <TableCell>Kdo</TableCell>
            <TableCell>Entita</TableCell>
            <TableCell>Akce</TableCell>
            <TableCell>Shrnutí</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => (
            <Row key={e.id} entry={e} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
